// src/components/dashboard/DashboardMetrics.jsx
import React, { useState, useRef, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import 'react-datepicker/dist/react-datepicker.css';
import { FiCalendar, FiChevronDown, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

registerLocale('fr', fr);

// ─── Constantes ───────────────────────────────────────────────────────────────
const MOIS_COURTS = ['Jan','Fév','Mar','Avr','Mai','Jun',
                     'Jul','Aoû','Sep','Oct','Nov','Déc'];
const MOIS_LONGS  = ['Janvier','Février','Mars','Avril','Mai','Juin',
                     'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const YEARS = [2023, 2024, 2025, 2026, 2027];

// ✅ Valeurs par défaut — évite tout crash si l'API ne répond pas
const DEFAULT_METRICS = {
  courtage:     { previsionnel: 0, encaisse: 0 },
  acheminement: { previsionnel: 0, encaisse: 0 },
  cspe:         { previsionnel: 0, encaisse: 0 },
  ticgn:        { previsionnel: 0, encaisse: 0 },
  global:       { previsionnel: 0, encaisse: 0 },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatButtonLabel(period) {
  if (!period) return 'Période';
  if (period.type === 'month' && period.year && period.month)
    return `${MOIS_LONGS[period.month - 1]} ${period.year}`;
  if (period.type === 'year' && period.year)
    return `Année ${period.year}`;
  if (period.type === 'custom' && period.startDate && period.endDate) {
    const s = new Date(period.startDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    const e = new Date(period.endDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${s} – ${e}`;
  }
  return 'Période';
}

function formatEuro(val) {
  const n = Number(val);
  if (isNaN(n)) return '0 €';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M €`;
  if (Math.abs(n) >= 1_000)     return `${(n / 1_000).toFixed(0)}k €`;
  return `${n.toFixed(0)} €`;
}

// ─── PeriodPicker ─────────────────────────────────────────────────────────────
const PeriodPicker = ({ selectedPeriod, onPeriodChange }) => {
  const [open, setOpen] = useState(false);
  const [tab,  setTab]  = useState('month');
  const [navYear, setNavYear] = useState(
    selectedPeriod?.year || new Date().getFullYear()
  );
  const [customRange, setCustomRange] = useState([null, null]);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectMonth = (month) => {
    onPeriodChange({ type: 'month', year: navYear, month });
    setOpen(false);
  };

  const selectYear = (year) => {
    onPeriodChange({ type: 'year', year });
    setOpen(false);
  };

  const handleCustomChange = (dates) => {
    const [start, end] = dates;
    setCustomRange([start, end]);
    if (start && end) {
      onPeriodChange({
        type: 'custom',
        startDate: start.toISOString(),
        endDate:   end.toISOString(),
      });
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-[10px] text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors shadow-sm"
      >
        <FiCalendar className="w-2.5 h-2.5 text-teal-600" />
        {formatButtonLabel(selectedPeriod)}
        <FiChevronDown className={`w-2.5 h-2.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-visible">

          {/* Onglets */}
          <div className="flex border-b border-gray-100">
            {['month','year','custom'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 py-1.5 text-[10px] font-medium transition-colors ${
                  tab === t
                    ? 'border-b-2 border-teal-500 text-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'month' ? 'Mois' : t === 'year' ? 'Année' : 'Plage'}
              </button>
            ))}
          </div>

          <div className="p-2">
            {/* Navigation année */}
            {tab !== 'custom' && (
              <div className="flex items-center justify-between mb-2">
                <button onClick={() => setNavYear(y => y - 1)} className="p-0.5 rounded hover:bg-gray-100">
                  <FiChevronLeft className="w-3 h-3 text-gray-500" />
                </button>
                <span className="text-[10px] font-semibold text-gray-700">{navYear}</span>
                <button onClick={() => setNavYear(y => y + 1)} className="p-0.5 rounded hover:bg-gray-100">
                  <FiChevronRight className="w-3 h-3 text-gray-500" />
                </button>
              </div>
            )}

            {/* Grille des mois */}
            {tab === 'month' && (
              <div className="grid grid-cols-4 gap-1">
                {MOIS_COURTS.map((m, i) => {
                  const active = selectedPeriod?.type === 'month'
                    && selectedPeriod.month === i + 1
                    && selectedPeriod.year  === navYear;
                  return (
                    <button
                      key={m}
                      onClick={() => selectMonth(i + 1)}
                      className={`py-1 rounded text-[10px] font-medium transition-colors ${
                        active
                          ? 'bg-teal-600 text-white'
                          : 'hover:bg-teal-50 text-gray-700'
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Grille des années */}
            {tab === 'year' && (
              <div className="grid grid-cols-3 gap-1">
                {YEARS.map(y => {
                  const active = selectedPeriod?.type === 'year' && selectedPeriod.year === y;
                  return (
                    <button
                      key={y}
                      onClick={() => selectYear(y)}
                      className={`py-1 rounded text-[10px] font-medium transition-colors ${
                        active
                          ? 'bg-teal-600 text-white'
                          : 'hover:bg-teal-50 text-gray-700'
                      }`}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Plage personnalisée */}
            {tab === 'custom' && (
              <div className="flex justify-center">
                <DatePicker
                  selected={customRange[0]}
                  onChange={handleCustomChange}
                  startDate={customRange[0]}
                  endDate={customRange[1]}
                  selectsRange
                  inline
                  locale="fr"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── DashboardMetrics ─────────────────────────────────────────────────────────
const DashboardMetrics = ({ selectedPeriod, onPeriodChange, metrics }) => {

  // ✅ FUSION avec les valeurs par défaut — protège contre undefined/null/structure partielle
  const safeMetrics = {
    courtage:     { ...DEFAULT_METRICS.courtage,     ...(metrics?.courtage     || {}) },
    acheminement: { ...DEFAULT_METRICS.acheminement, ...(metrics?.acheminement || {}) },
    cspe:         { ...DEFAULT_METRICS.cspe,         ...(metrics?.cspe         || {}) },
    ticgn:        { ...DEFAULT_METRICS.ticgn,        ...(metrics?.ticgn        || {}) },
    global:       { ...DEFAULT_METRICS.global,       ...(metrics?.global       || {}) },
  };

  // Pourcentage global encaissé / prévisionnel
  const pct = safeMetrics.global.previsionnel > 0
    ? Math.round((safeMetrics.global.encaisse / safeMetrics.global.previsionnel) * 100)
    : 0;

  const sections = [
    {
      key:    'courtage',
      label:  'Courtage',
      color:  'border-teal-400',
      textColor: 'text-teal-700',
      bgColor:   'bg-teal-50',
      data:   safeMetrics.courtage,
    },
    {
      key:    'acheminement',
      label:  'Acheminement',
      color:  'border-blue-400',
      textColor: 'text-blue-700',
      bgColor:   'bg-blue-50',
      data:   safeMetrics.acheminement,
    },
    {
      key:    'cspe',
      label:  'CSPE',
      color:  'border-amber-400',
      textColor: 'text-amber-700',
      bgColor:   'bg-amber-50',
      data:   safeMetrics.cspe,
    },
    {
      key:    'ticgn',
      label:  'TICGN',
      color:  'border-orange-400',
      textColor: 'text-orange-700',
      bgColor:   'bg-orange-50',
      data:   safeMetrics.ticgn,
    },
    {
      key:    'global',
      label:  'Total Global',
      color:  'border-indigo-400',
      textColor: 'text-indigo-700',
      bgColor:   'bg-indigo-50',
      data:   safeMetrics.global,
      showPct: true,
    },
  ];

  return (
    // ✅ PAS de overflow-hidden ici → le dropdown PeriodPicker s'affiche correctement
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">

      {/* En-tête */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
        <h2 className="text-[10px] font-semibold text-gray-700 uppercase tracking-wider">
          Aperçu Financier
        </h2>
        <PeriodPicker
          selectedPeriod={selectedPeriod}
          onPeriodChange={onPeriodChange}
        />
      </div>

      {/* KPI bar — 5 colonnes */}
      <div className="grid grid-cols-5 divide-x divide-gray-100 rounded-b-lg overflow-hidden">
        {sections.map(({ key, label, color, textColor, bgColor, data, showPct }) => (
          <div key={key} className={`px-3 py-2 border-t-2 ${color}`}>

            {/* Label section */}
            <p className={`text-[9px] font-semibold uppercase tracking-wider mb-1 ${textColor}`}>
              {label}
            </p>

            {/* Prévisionnel */}
            <div className="flex items-baseline gap-1 mb-0.5">
              <span className="text-[8px] text-gray-400 w-6 flex-shrink-0">Prév.</span>
              <span className="text-sm font-bold text-gray-800 leading-none">
                {formatEuro(data.previsionnel)}
              </span>
            </div>

            {/* Encaissé */}
            <div className="flex items-baseline gap-1">
              <span className="text-[8px] text-gray-400 w-6 flex-shrink-0">Enc.</span>
              <span className="text-sm font-bold leading-none" style={{ color: '#0d9488' }}>
                {formatEuro(data.encaisse)}
              </span>
              {showPct && (
                <span className={`ml-1 px-1 py-0.5 rounded text-[9px] font-semibold ${bgColor} ${textColor}`}>
                  {pct}%
                </span>
              )}
            </div>

          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardMetrics;
